import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ChevronSelectorVerticalIcon,
  LogOut01Icon,
  Settings01Icon,
  User01Icon,
  Users01Icon,
} from '@untitledui-icons/react/line'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { useAuth } from '@/hooks/api/useAuth'
import { ThemeToggle } from './ThemeToggle'
import { LanguageSelector } from './LanguageSelector'
import { Avatar } from '@/components/common/Avatar'
import { cn } from '@/lib/utils'

interface UserMenuProps {
  /**
   * Whether the sidebar is collapsed
   */
  collapsed?: boolean
}

export function UserMenu({ collapsed = false }: UserMenuProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const user = useAuthStore((state) => state.user)
  const setMobileMenuOpen = useUIStore((state) => state.setMobileMenuOpen)
  const { logout } = useAuth()

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const handleNavigate = (path: string) => {
    navigate(path)
    setIsOpen(false)
    setMobileMenuOpen(false)
  }

  const handleLogout = () => {
    setIsOpen(false)
    logout() // This clears auth state, React Query cache, and navigates to /login
  }

  return (
    <div ref={menuRef} className="relative w-full">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-gray-200 p-2 text-left transition-colors',
          'hover:bg-gray-50',
          'dark:border-gray-700 dark:hover:bg-gray-800',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          collapsed && 'justify-center'
        )}
      >
        {/* Avatar */}
        <Avatar name={user?.fullName || t('resources.generic.user_fallback')} size="sm" />

        {!collapsed && (
          <>
            {/* User info */}
            <div className="flex-1 overflow-hidden">
              <p className="truncate text-sm font-medium text-gray-700 dark:text-gray-200">
                {user?.fullName || t('resources.generic.user_fallback')}
              </p>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                {user?.email || ''}
              </p>
            </div>

            {/* Chevron */}
            <ChevronSelectorVerticalIcon className="h-4 w-4 shrink-0 text-gray-400" />
          </>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute z-50 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg',
            'dark:border-gray-700 dark:bg-gray-900',
            collapsed ? 'bottom-0 left-full ml-2' : 'bottom-full left-0 mb-2'
          )}
        >
          {/* User info header */}
          <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-800">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {user?.fullName || t('resources.generic.user_fallback')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {user?.email || ''}
            </p>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <button
              type="button"
              onClick={() => handleNavigate('/profile')}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <User01Icon className="h-4 w-4" />
              <span>{t('resources.generic.my_profile')}</span>
            </button>

            <button
              type="button"
              onClick={() => handleNavigate('/users')}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <Users01Icon className="h-4 w-4" />
              <span>{t('resources.generic.users')}</span>
            </button>

            <button
              type="button"
              onClick={() => handleNavigate('/profile')}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <Settings01Icon className="h-4 w-4" />
              <span>{t('resources.generic.settings')}</span>
            </button>
          </div>

          {/* Language selector */}
          <div className="border-t border-gray-100 py-1 dark:border-gray-800">
            <div className="px-2 py-1">
              <LanguageSelector showLabel={true} variant="default" />
            </div>
          </div>

          {/* Theme toggle */}
          <div className="border-t border-gray-100 py-1 dark:border-gray-800">
            <div className="px-4 py-2">
              <ThemeToggle />
            </div>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 py-1 dark:border-gray-800">
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-error-600 hover:bg-error-50 dark:text-error-400 dark:hover:bg-error-950"
            >
              <LogOut01Icon className="h-4 w-4" />
              <span>{t('navigation.logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserMenu
